import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { X, Plus, Trash2, Baby, Phone, AlertTriangle, ExternalLink, Calendar, Clock } from 'lucide-react';
import NumberFormat from 'react-number-format';
import { LanguageContext } from '../../../../Context';
import InlineLocationSelect from '../../components/InlineLocationSelect';
import ClassSelect from './ClassSelect';


/**
 * LeadDialog - Modal dialog for adding or editing a lead.
 * Phone-centric creation flow with duplicate detection, formatted phone input.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Function} props.onSave - Callback with lead data to save
 * @param {Object|null} props.lead - Existing lead to edit, or null for new
 * @param {string} props.defaultStage - Default stage for new leads
 * @param {Function} props.onCheckPhone - Async function that returns duplicate leads for a phone number
 * @param {Function} props.onViewLead - Callback to navigate to an existing lead
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 2.2
 * @since 2026-04-15
 */
export default function LeadDialog({ open, onClose, onSave, lead, defaultStage, onCheckPhone, onViewLead }) {
  const { l, language } = useContext(LanguageContext);
  

  const t = useCallback((key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  }, [l, language]);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    zalo: '',
    email: '',
    source: '',
    priority: 'medium',
    notes: '',
    addedBy: '',
    children: [],
    stage: 'lead',
    testDate: '',
    testTime: '',
    testLocation: '',
    testStatus: '',
    placementResult: '',
  });

  const [phoneCheck, setPhoneCheck] = useState({ checking: false, duplicates: [], acknowledged: false });
  const debounceRef = useRef(null);
  const phoneInputRef = useRef(null);
  const hasAutoFocused = useRef(false);

  useEffect(() => {
    if (lead) {
      setForm({
        name: lead.name || '',
        phone: lead.phone || '',
        zalo: lead.zalo || '',
        email: lead.email || '',
        source: lead.source || '',
        priority: lead.priority || 'medium',
        notes: lead.notes || '',
        addedBy: lead.addedBy || '',
        children: lead.children?.length ? lead.children.map(c => ({ ...c })) : [],
        stage: lead.stage || 'lead',
        testDate: lead.testDate || '',
        testTime: lead.testTime || '',
        testLocation: lead.testLocation || '',
        testStatus: lead.testStatus || '',
        placementResult: lead.placementResult || '',
      });
      setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
    } else {
      setForm({
        name: '',
        phone: '',
        zalo: '',
        email: '',
        source: '',
        priority: 'medium',
        notes: '',
        addedBy: '',
        children: [],
        stage: defaultStage || 'lead',
        testDate: '',
        testTime: '',
        testLocation: '',
        testStatus: '',
        placementResult: '',
      });
      setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
    }
  }, [lead, defaultStage, open]);

  // Auto-focus phone on first open only
  useEffect(() => {
    if (open && !hasAutoFocused.current) {
      hasAutoFocused.current = true;
      setTimeout(() => phoneInputRef.current?.focus(), 50);
    }
    if (!open) hasAutoFocused.current = false;
  }, [open]);

  const checkDuplicatePhone = useCallback(async (phone) => {
    if (!phone.trim() || !onCheckPhone || lead) {
      setPhoneCheck(prev => ({ ...prev, checking: false, duplicates: [] }));
      return;
    }
    setPhoneCheck(prev => ({ ...prev, checking: true }));
    try {
      const result = await onCheckPhone(phone.trim());
      const duplicates = result?.leads || [];
      setPhoneCheck({ checking: false, duplicates, acknowledged: false });
    } catch {
      setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
    }
  }, [onCheckPhone, lead]);

  const handlePhoneValueChange = useCallback((values) => {
    const raw = values.value;
    setForm(prev => ({ ...prev, phone: raw }));
    setPhoneCheck(prev => ({ ...prev, acknowledged: false }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (raw.length >= 8) {
      debounceRef.current = setTimeout(() => checkDuplicatePhone(raw), 400);
    } else {
      setPhoneCheck({ checking: false, duplicates: [], acknowledged: false });
    }
  }, [checkDuplicatePhone]);

  const handlePhoneBlur = useCallback(() => {
    if (form.phone.length >= 8 && !lead && phoneCheck.duplicates.length === 0 && !phoneCheck.checking) {
      checkDuplicatePhone(form.phone);
    }
  }, [form.phone, lead, phoneCheck, checkDuplicatePhone]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const addChild = () => {
    setForm(prev => ({
      ...prev,
      children: [...prev.children, { name: '', age: '', currentLevel: '', notes: '' }],
    }));
  };

  const updateChild = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      children: prev.children.map((child, i) =>
        i === index ? { ...child, [field]: value } : child
      ),
    }));
  };

  const removeChild = (index) => {
    setForm(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index),
    }));
  };

  const hasDuplicates = phoneCheck.duplicates.length > 0 && !phoneCheck.acknowledged;
  const canSubmit = form.phone.trim().length > 0 && !phoneCheck.checking && !hasDuplicates;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.phone.trim()) return;
    onSave(form);
    onClose();
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const daysSince = (iso) => {
    if (!iso) return null;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  const stageLabels = {
    'lead': t('pipeline.lead', 'Lead'),
    'add-zalo': t('pipeline.addZalo', 'Add Zalo'),
    'book-test': t('pipeline.bookTest', 'Book Test'),
    'test': t('pipeline.test', 'Test'),
    'enrol': t('pipeline.enrol', 'Enrol'),
  };

  const sourceOptions = [
    { value: 'facebook', label: t('pipeline.facebook', 'Facebook') },
    { value: 'referral', label: t('pipeline.referral', 'Referral') },
    { value: 'walk-in', label: t('pipeline.walkIn', 'Walk-in') },
    { value: 'website', label: t('pipeline.website', 'Website') },
    { value: 'zalo-ads', label: t('pipeline.zaloAds', 'Zalo Ads') },
    { value: 'google', label: t('pipeline.google', 'Google') },
    { value: 'other', label: t('pipeline.other', 'Other') },
  ];

  const inputClass = `w-full px-3 py-2 rounded-[10px] border border-slate-200 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800/20 transition-colors duration-150`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">
            {lead ? t('pipeline.editLead', 'Edit Lead') : t('pipeline.newLead', 'New Lead')}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">

            {/* Phone — primary field */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('pipeline.phoneRequired', 'Phone *')}
              </label>
              <div className="relative">
                <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <NumberFormat
                  format="###-###-####"
                  mask="_"
                  value={form.phone}
                  onValueChange={handlePhoneValueChange}
                  onBlur={handlePhoneBlur}
                  getInputRef={phoneInputRef}
                  className={`${inputClass} pl-9 pr-3 py-2.5`}
                  placeholder="___-___-____"
                />
                {phoneCheck.checking && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className={`w-4 h-4 border-2 border-slate-200 border-t-teal-800 rounded-full animate-spin`} />
                  </div>
                )}
              </div>

              {/* Duplicate warning */}
              {hasDuplicates && (
                <div className="mt-2 p-3 rounded-[10px] bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-800 mb-1.5">
                        {phoneCheck.duplicates.length === 1
                          ? t('pipeline.duplicateFound', 'A lead with this phone already exists')
                          : `${phoneCheck.duplicates.length} ${t('pipeline.duplicatesFound', 'leads with this phone already exist')}`
                        }
                      </p>
                      {phoneCheck.duplicates.map((dup) => {
                        const days = daysSince(dup.lastContactDate);
                        return (
                          <div key={dup._id} className="flex items-center gap-2 mb-1.5">
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-amber-700 font-medium truncate">
                                {dup.name || t('pipeline.noName', 'No name')}
                              </p>
                              <p className="text-[10px] text-amber-600">
                                {stageLabels[dup.stage] || dup.stage}
                                {days !== null && days !== undefined && (
                                  <span> · {t('pipeline.lastContact', 'last contact')} {days === 0 ? t('pipeline.today', 'Today').toLowerCase() : `${days}${t('pipeline.dayAgo', 'd')} ${t('pipeline.lastContact', 'last contact')}`}</span>
                                )}
                              </p>
                            </div>
                            {onViewLead && (
                              <button
                                type="button"
                                onClick={() => onViewLead(dup)}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-amber-700 hover:bg-amber-100 transition-colors duration-150 cursor-pointer"
                              >
                                <ExternalLink size={10} />
                                {t('pipeline.viewExisting', 'View')}
                              </button>
                            )}
                          </div>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setPhoneCheck(prev => ({ ...prev, acknowledged: true }))}
                        className="mt-1 text-xs font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2 transition-colors duration-150 cursor-pointer"
                      >
                        {t('pipeline.createAnyway', 'Create anyway')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Name — optional */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('pipeline.name', 'Name')}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={inputClass}
                placeholder={t('pipeline.customerName', 'Customer name (optional)')}
              />
            </div>

            {/* Added By + Source row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('pipeline.addedBy', 'Added By')}
                </label>
                <input
                  type="text"
                  value={form.addedBy}
                  onChange={(e) => updateField('addedBy', e.target.value)}
                  className={inputClass}
                  placeholder={t('pipeline.yourName', 'Your name')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('pipeline.source', 'Source')}
                </label>
                <select
                  value={form.source}
                  onChange={(e) => updateField('source', e.target.value)}
                  className={`${inputClass} bg-white`}
                >
                  <option value="">{t('pipeline.selectSource', 'Select source')}</option>
                  {sourceOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Zalo + Email row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('pipeline.zalo', 'Zalo')}
                </label>
                <input
                  type="text"
                  value={form.zalo}
                  onChange={(e) => updateField('zalo', e.target.value)}
                  className={inputClass}
                  placeholder={t('pipeline.zaloNumber', 'Zalo number')}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                  {t('pipeline.email', 'Email')}
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={inputClass}
                  placeholder={t('pipeline.emailAddress', 'Email address')}
                />
              </div>
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('pipeline.priority', 'Priority')}
              </label>
              <div className="flex gap-2">
                {['low', 'medium', 'high'].map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => updateField('priority', p)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-all duration-150 border ${
                      form.priority === p
                        ? p === 'high'
                          ? 'bg-red-50 border-red-300 text-red-700'
                          : p === 'medium'
                            ? 'bg-amber-50 border-amber-300 text-amber-700'
                            : 'bg-green-50 border-green-300 text-green-700'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {t(`pipeline.${p}`, p)}
                  </button>
                ))}
              </div>
            </div>

            {/* Test scheduling — shown for book-test and later stages */}
            {['book-test', 'test', 'enrol'].includes(form.stage) && (
              <div className="p-3 rounded-[10px] border border-amber-200 bg-amber-50/50 space-y-3">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">
                  {t('pipeline.testScheduling', 'Test Scheduling')}
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      {t('pipeline.testDate', 'Test Date')}
                    </label>
                    <div className="relative">
                      <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="date"
                        value={form.testDate}
                        onChange={(e) => updateField('testDate', e.target.value)}
                        className={`${inputClass} pl-8`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      {t('pipeline.testTime', 'Test Time')}
                    </label>
                    <div className="relative">
                      <Clock size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                      <input
                        type="time"
                        value={form.testTime}
                        onChange={(e) => updateField('testTime', e.target.value)}
                        className={`${inputClass} pl-8`}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      {t('pipeline.testLocation', 'Location')}
                    </label>
                    <InlineLocationSelect
                      value={form.testLocation}
                      setValue={(val) => updateField('testLocation', val)}
                      size="small"
                      placeholder={t('pipeline.selectLocation', 'Select location')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Test outcome — shown for test and enrol stages */}
            {['test', 'enrol'].includes(form.stage) && (
              <div className="p-3 rounded-[10px] border border-orange-200 bg-orange-50/50 space-y-3">
                <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
                  {t('pipeline.testOutcome', 'Test Outcome')}
                </p>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    {t('pipeline.testStatus', 'Status')}
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: 'scheduled', label: t('pipeline.scheduled', 'Scheduled') },
                      { key: 'completed', label: t('pipeline.completed', 'Completed') },
                      { key: 'no-show', label: t('pipeline.noShow', 'No-show') },
                      { key: 'rescheduled', label: t('pipeline.rescheduled', 'Rescheduled') },
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => updateField('testStatus', form.testStatus === key ? '' : key)}
                        className={`px-3 py-1.5 rounded-[10px] text-xs font-semibold border transition-all duration-150 cursor-pointer ${
                          form.testStatus === key
                            ? 'border-current shadow-sm'
                            : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                        }`}
                        style={form.testStatus === key
                          ? { color: key === 'scheduled' ? '#3b82f6' : key === 'completed' ? '#10b981' : key === 'no-show' ? '#ef4444' : '#f59e0b',
                              backgroundColor: key === 'scheduled' ? '#eff6ff' : key === 'completed' ? '#ecfdf5' : key === 'no-show' ? '#fef2f2' : '#fffbeb',
                              borderColor: key === 'scheduled' ? '#bfdbfe' : key === 'completed' ? '#a7f3d0' : key === 'no-show' ? '#fecaca' : '#fde68a' }
                          : {}}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <ClassSelect
                  value={form.placementResult}
                  onChange={(val) => updateField('placementResult', val)}
                  testLocation={form.testLocation}
                  label={t('pipeline.selectClass', 'Select Class')}
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                {t('pipeline.notes', 'Notes')}
              </label>
              <textarea
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={2}
                className={`${inputClass} resize-none`}
                placeholder={t('pipeline.notesPlaceholder', 'Notes about this lead...')}
              />
            </div>

            {/* Children section */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {t('pipeline.children', 'Children')}
                </label>
                <button
                  type="button"
                  onClick={addChild}
                  className={`flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900 transition-colors duration-150 cursor-pointer`}
                >
                  <Plus size={13} />
                  {t('pipeline.addChild', 'Add child')}
                </button>
              </div>

              <div className="space-y-2">
                {form.children.map((child, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 p-3 rounded-[10px] bg-slate-50 border border-slate-100"
                  >
                    <Baby size={16} className="text-slate-400 mt-1 flex-shrink-0" />
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={child.name}
                        onChange={(e) => updateChild(index, 'name', e.target.value)}
                        className={`px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-800 focus:outline-none focus:border-teal-800 transition-colors duration-150`}
                        placeholder={t('pipeline.name', 'Name')}
                      />
                      <input
                        type="text"
                        value={child.age}
                        onChange={(e) => updateChild(index, 'age', e.target.value)}
                        className={`px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-800 focus:outline-none focus:border-teal-800 transition-colors duration-150`}
                        placeholder={t('pipeline.age', 'Age')}
                      />
                      <input
                        type="text"
                        value={child.currentLevel}
                        onChange={(e) => updateChild(index, 'currentLevel', e.target.value)}
                        className={`px-2 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-800 focus:outline-none focus:border-teal-800 transition-colors duration-150`}
                        placeholder={t('pipeline.level', 'Level')}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeChild(index)}
                      className="w-6 h-6 rounded flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors duration-150 cursor-pointer"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-[10px] text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
            >
              {t('pipeline.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`px-5 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 ${
                canSubmit ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
              }`}
            >
              {lead ? t('pipeline.update', 'Update') : t('pipeline.save', 'Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
